import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useClient, useUpdateClient, useDeleteClient } from '@/features/clients/hooks/useClients'
import { useClientCustomers, useCreateCustomer } from '@/features/customers/hooks/useCustomers'
import { ClientForm } from '@/features/clients/components/ClientForm'
import { CustomerForm } from '@/features/customers/components/CustomerForm'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { ArrowLeft, Edit, Trash2, Plus, Search, ArrowUp, ArrowDown } from 'lucide-react'
import { useClientInvoices } from '@/features/invoices/hooks/useInvoices'
import { InvoiceDetailModal } from '@/features/invoices/components/InvoiceDetailModal'
import { AgingBadge } from '@/features/invoices/components/AgingBadge'
import { useClientPayments } from '@/features/payments/hooks/usePayments'
import { useMemo } from 'react'
import { formatCurrency, formatDate } from '@/lib/format'
import { QuickBooksCard } from '@/features/quickbooks/components/QuickBooksCard'
import { DetectedPaymentsList } from '@/features/quickbooks/components/DetectedPaymentsList'
import { SourceBadge } from '@/features/quickbooks/components/SourceBadge'
import { calculateDaysPastDue } from '@/utils/aging'
import { useQBODetectedPayments } from '@/features/quickbooks/hooks/useQuickBooks'
import { AlertCircle } from 'lucide-react'

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: client, isLoading } = useClient(id)
  const { data: customers } = useClientCustomers(id)
  const { data: invoices, error: invoicesError } = useClientInvoices(id)
  const { data: allPayments } = useClientPayments(id)
  const { data: detectedPayments } = useQBODetectedPayments(id)
  const updateClient = useUpdateClient()
  const deleteClient = useDeleteClient()
  const createCustomer = useCreateCustomer()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null)
  const [deleteConfirmed, setDeleteConfirmed] = useState(false)
  const [reportsAcknowledged, setReportsAcknowledged] = useState(false)
  const [activeTab, setActiveTab] = useState<'customers' | 'invoices'>('customers')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Filtered and sorted customers - must be before early returns
  const filteredCustomers = useMemo(() => {
    if (!customers) return []
    let filtered = customers

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Sort
    if (sortColumn) {
      filtered = [...filtered].sort((a: any, b: any) => {
        const aVal = a[sortColumn]
        const bVal = b[sortColumn]
        if (aVal == null) return 1
        if (bVal == null) return -1
        const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0
        return sortDirection === 'asc' ? comparison : -comparison
      })
    }

    return filtered
  }, [customers, searchTerm, sortColumn, sortDirection])

  // Filtered and sorted invoices - must be before early returns
  const filteredInvoices = useMemo(() => {
    if (!invoices) return []
    let filtered = invoices

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((inv: any) =>
        inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Sort
    if (sortColumn) {
      filtered = [...filtered].sort((a: any, b: any) => {
        // For aging/days_past_due, compute from shared aging function (not stale stored field)
        let aVal = sortColumn === 'days_past_due' ? calculateDaysPastDue(a) : a[sortColumn]
        let bVal = sortColumn === 'days_past_due' ? calculateDaysPastDue(b) : b[sortColumn]

        if (aVal == null) return 1
        if (bVal == null) return -1
        const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0
        return sortDirection === 'asc' ? comparison : -comparison
      })
    }

    return filtered
  }, [invoices, searchTerm, sortColumn, sortDirection])

  // Calculate current AR dynamically from all invoice balances
  const currentAR = useMemo(() => {
    if (!invoices) return 0
    return invoices.reduce((sum: number, inv: any) => sum + (inv.balance_due || 0), 0)
  }, [invoices])

  // Calculate Total AR Managed (Starting AR + all invoices added after start)
  const totalARManaged = useMemo(() => {
    if (!invoices || !client) return client?.baseline_ar_total || 0
    const startingAR = client.baseline_ar_total || 0
    const newInvoicesTotal = invoices.reduce((sum: number, inv: any) => {
      // Only count invoices added after contract start
      // Use string comparison to avoid timezone bugs
      if (inv.invoice_date > client.contract_start_date) {
        return sum + (inv.original_amount || 0)
      }
      return sum
    }, 0)
    return startingAR + newInvoicesTotal
  }, [invoices, client])

  // Calculate AR Collected (sum of all confirmed payments, excluding pre-contract)
  const arCollected = useMemo(() => {
    if (!allPayments) return 0
    return allPayments
      .filter((payment: any) => !payment.is_pre_contract)
      .reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0)
  }, [allPayments])

  // Calculate Total Fees (sum of collection_fee_owed for non-pre-contract payments)
  const totalFees = useMemo(() => {
    if (!allPayments) return 0
    return allPayments
      .filter((payment: any) => !payment.is_pre_contract)
      .reduce((sum: number, payment: any) => sum + (payment.collection_fee_owed || 0), 0)
  }, [allPayments])

  // Calculate Fees Paid (not tracked yet, returns $0.00 for now)
  const feesPaid = useMemo(() => {
    // TODO: Wire to real fee payment tracking in next session
    return 0
  }, [])

  // Calculate Fees Owed (Total Fees - Fees Paid)
  const feesOwed = useMemo(() => {
    return totalFees - feesPaid
  }, [totalFees, feesPaid])

  // Early returns AFTER all hooks
  if (isLoading) return <div>Loading...</div>
  if (!client) return <div>Client not found</div>

  const handleUpdate = async (data: any) => {
    await updateClient.mutateAsync({ id: client.id, updates: data })
    setShowEditModal(false)
  }

  const handleDelete = async () => {
    if (!deleteConfirmed) {
      alert('Please confirm that you want to delete this client')
      return
    }
    if (!reportsAcknowledged) {
      alert('Please acknowledge that you have run all final reports')
      return
    }

    try {
      await deleteClient.mutateAsync(client.id)
      navigate('/clients')
    } catch (error) {
      console.error('Failed to delete client:', error)
      alert(`Failed to delete client: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleCreateCustomer = async (data: any) => {
    await createCustomer.mutateAsync({ ...data, client_id: client.id })
    setShowCustomerModal(false)
  }

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/clients')}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
            <p className="text-sm text-gray-600">Client Details</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => setShowEditModal(true)}><Edit className="h-4 w-4 mr-2" />Edit</Button>
          <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
        </div>
      </div>

      {/* Pending Payments Banner */}
      {detectedPayments && detectedPayments.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">
                {detectedPayments.length} payment{detectedPayments.length === 1 ? '' : 's'} detected from QuickBooks
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Total: {formatCurrency(detectedPayments.reduce((sum: number, p: any) => sum + p.amount, 0))} pending confirmation
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // Scroll to detected payments section
                document.querySelector('.detected-payments-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              Review
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <Card>
            <h2 className="text-lg font-semibold mb-4">Client Information</h2>
            <dl className="grid grid-cols-2 gap-4">
              <div><dt className="text-sm font-medium text-gray-500">Contact Person</dt><dd className="mt-1 text-sm text-gray-900">{client.contact_person || '—'}</dd></div>
              <div><dt className="text-sm font-medium text-gray-500">Phone</dt><dd className="mt-1 text-sm text-gray-900">{client.phone || '—'}</dd></div>
              <div><dt className="text-sm font-medium text-gray-500">Email</dt><dd className="mt-1 text-sm text-gray-900">{client.email || '—'}</dd></div>
              <div><dt className="text-sm font-medium text-gray-500">Status</dt><dd className="mt-1"><Badge variant={client.status === 'active' ? 'success' : 'default'}>{client.status}</Badge></dd></div>
              <div><dt className="text-sm font-medium text-gray-500">Contract Start</dt><dd className="mt-1 text-sm text-gray-900">{formatDate(client.contract_start_date)}</dd></div>
              <div><dt className="text-sm font-medium text-gray-500">Created</dt><dd className="mt-1 text-sm text-gray-900">{formatDate(client.created_at)}</dd></div>
            </dl>
          </Card>
        </div>
        <div>
          <Card>
            <h2 className="text-lg font-semibold mb-4">Financial</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Current AR</dt>
                <dd className="mt-1 text-2xl font-bold text-primary-600">
                  {invoicesError ? (
                    <span className="text-red-600 text-sm flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Error loading
                    </span>
                  ) : (
                    formatCurrency(currentAR)
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Starting AR</dt>
                <dd className="mt-1 text-lg text-gray-900">{formatCurrency(client.baseline_ar_total || 0)}</dd>
                <dd className="mt-0.5 text-xs text-gray-500">AR when we began managing this client</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Total AR Managed</dt>
                <dd className="mt-1 text-lg text-gray-900">{formatCurrency(totalARManaged)}</dd>
                <dd className="mt-0.5 text-xs text-gray-500">Starting AR + all invoices added</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">AR Collected</dt>
                <dd className="mt-1 text-lg font-semibold text-green-600">{formatCurrency(arCollected)}</dd>
                <dd className="mt-0.5 text-xs text-gray-500">Total payments received (excluding pre-contract)</dd>
              </div>
              <div className="pt-3 border-t">
                <dt className="text-sm font-medium text-gray-500">Total Fees</dt>
                <dd className="mt-1 text-lg text-gray-900">{formatCurrency(totalFees)}</dd>
                <dd className="mt-0.5 text-xs text-gray-500">8% on all fee-bearing payments (excluding pre-contract)</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fees Paid</dt>
                <dd className="mt-1 text-lg text-gray-900">{formatCurrency(feesPaid)}</dd>
                <dd className="mt-0.5 text-xs text-gray-500">Amount client has paid HeartWest (not tracked yet)</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fees Owed</dt>
                <dd className="mt-1 text-lg font-semibold text-orange-600">{formatCurrency(feesOwed)}</dd>
                <dd className="mt-0.5 text-xs text-gray-500">Total Fees - Fees Paid</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>

      {/* QuickBooks Integration */}
      <QuickBooksCard clientId={id!} />

      {/* Detected Payments */}
      <div className="detected-payments-section">
        <DetectedPaymentsList clientId={id!} />
      </div>

      <Card>
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <div className="flex items-center justify-between">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setActiveTab('customers')
                  setSearchTerm('')
                  setSortColumn('')
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'customers'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Customers ({customers?.length || 0})
              </button>
              <button
                onClick={() => {
                  setActiveTab('invoices')
                  setSearchTerm('')
                  setSortColumn('invoice_number')
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'invoices'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Invoices ({invoices?.length || 0})
              </button>
            </nav>
            {activeTab === 'customers' && (
              <Button onClick={() => setShowCustomerModal(true)}>
                <Plus className="h-4 w-4 mr-2" />Add Customer
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Content */}
        {activeTab === 'customers' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer Name</span>
                      {sortColumn === 'name' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('phone')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Phone</span>
                      {sortColumn === 'phone' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('email')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Email</span>
                      {sortColumn === 'email' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('contact_preference')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Preferred Contact</span>
                      {sortColumn === 'contact_preference' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('source')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Source</span>
                      {sortColumn === 'source' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      {searchTerm ? 'No customers found' : 'No customers yet'}
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer: any) => (
                    <tr
                      key={customer.id}
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{customer.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.phone || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.email || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {customer.contact_preference ? <Badge>{customer.contact_preference}</Badge> : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <SourceBadge source={customer.source || 'manual'} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('invoice_number')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Invoice #</span>
                      {sortColumn === 'invoice_number' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th
                    onClick={() => handleSort('invoice_date')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date</span>
                      {sortColumn === 'invoice_date' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('due_date')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Due Date</span>
                      {sortColumn === 'due_date' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('original_amount')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Amount</span>
                      {sortColumn === 'original_amount' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('balance_due')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Balance</span>
                      {sortColumn === 'balance_due' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('days_past_due')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Aging</span>
                      {sortColumn === 'days_past_due' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('source')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Source</span>
                      {sortColumn === 'source' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoicesError ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2 text-red-600">
                        <AlertCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">
                          Error loading invoices: {invoicesError.message}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-sm text-gray-500">
                      {searchTerm ? 'No invoices found' : 'No invoices yet'}
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice: any) => (
                    <tr
                      key={invoice.id}
                      onClick={() => setSelectedInvoice(invoice)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.invoice_number || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.customer?.name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.invoice_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.due_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(invoice.original_amount || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(invoice.balance_due || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <AgingBadge daysPastDue={calculateDaysPastDue(invoice)} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"><Badge>{invoice.status || 'unknown'}</Badge></td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <SourceBadge source={invoice.source || 'manual'} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Client" size="lg">
        <ClientForm initialData={client} onSubmit={handleUpdate} onCancel={() => setShowEditModal(false)} loading={updateClient.isPending} />
      </Modal>

      <Modal isOpen={showCustomerModal} onClose={() => setShowCustomerModal(false)} title="Add Customer" size="lg">
        <CustomerForm onSubmit={handleCreateCustomer} onCancel={() => setShowCustomerModal(false)} loading={createCustomer.isPending} />
      </Modal>

      <Modal isOpen={showDeleteConfirm} onClose={() => {
        setShowDeleteConfirm(false)
        setDeleteConfirmed(false)
        setReportsAcknowledged(false)
      }} title="Delete Client">
        <div>
          <p className="text-gray-700 mb-6">
            You are about to delete <strong>{client.name}</strong>. This action cannot be undone and will permanently delete:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
            <li>All customers under this client</li>
            <li>All invoices and payment records</li>
            <li>All contact logs and payment plans</li>
            <li>All monthly reports</li>
          </ul>

          <div className="space-y-4 mb-6">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteConfirmed}
                onChange={(e) => setDeleteConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                <strong className="text-red-600">* Required:</strong> I understand this will permanently delete all data for {client.name} and this action cannot be undone.
              </span>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={reportsAcknowledged}
                onChange={(e) => setReportsAcknowledged(e.target.checked)}
                className="mt-1 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <span className="text-sm text-gray-700">
                <strong className="text-amber-600">* Required:</strong> I have run and saved all final reports needed for this client.
              </span>
            </label>
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="secondary" onClick={() => {
              setShowDeleteConfirm(false)
              setDeleteConfirmed(false)
              setReportsAcknowledged(false)
            }}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteClient.isPending}
              disabled={!deleteConfirmed || !reportsAcknowledged}
            >
              Delete Client
            </Button>
          </div>
        </div>
      </Modal>

      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          payments={allPayments || []}
          isOpen={!!selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  )
}
